import React from "react"

import type { AudioRegionReviewConfig } from "@/metrics/runtime"

import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import { ThresholdMetricBox } from "@/components/ThresholdMetricBox"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
  SubvalueByTimestamp,
} from "@/types/RunMetricOutput"

import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { getStat } from "../helpers/getStat"
import { parseResultJson } from "../helpers/parseResultJson"

interface LatencyMeasurement {
  latencyInSeconds: number
  silenceStart: number
  silenceEnd: number
  toSpeaker: string
}

const DOCUMENTATION = `
This metric measures the latency of responses after filtering outliers, based on diarized audio analysis.
It calculates the response time between speaker turns in conversations.
`

const CATEGORY = "latency"

// Minimum latency threshold in seconds - values below this won't be displayed
const MIN_LATENCY_THRESHOLD = 0.1

interface LatencyStatistics {
  median_latency?: number
  min_latency?: number
  max_latency?: number
  std_dev?: number
  average_latency?: number
  mean_latency?: number
  distribution_metrics?: {
    percentiles?: {
      p90?: number
    }
  }
  outlier_analysis?: {
    metadata?: {
      outlier_count?: number
    }
  }
}

interface RawLatencyMeasurement extends Record<string, unknown> {
  latency?: number
  silence_start?: number
  silence_end?: number
  to_speaker?: string
}

interface LatencyResultJson {
  latency_analysis?: {
    statistics?: LatencyStatistics
    latencies?: RawLatencyMeasurement[]
  }
  filter_role?: string
}

function secondsToMs(seconds: number): number {
  return Math.floor(seconds * 1000)
}

interface LatencyStatsDisplay {
  medianLatency: number
  p90Latency: number
  minLatency: number
  maxLatency: number
  stdDev: number
  numOutliers: number
  meanLatency: number
}

const STAT_NAME = "latency_seconds"

function buildStatsFromTyped(
  mo: RunMetricOutput | MetricOutputDisplaySource,
): LatencyStatsDisplay | null {
  const summary = getStat<{
    min?: number
    max?: number
    mean?: number
    median?: number
  }>(mo, "summary", STAT_NAME)
  const dispersion = getStat<{ std_dev?: number }>(mo, "dispersion", STAT_NAME)
  const percentiles = getStat<{ p90?: number }>(mo, "percentiles", STAT_NAME)
  const outliers = getStat<{ count?: number }>(mo, "outliers", STAT_NAME)

  if (!summary && !dispersion && !percentiles && !outliers) {
    return null
  }

  return {
    medianLatency: secondsToMs(summary?.median ?? 0),
    p90Latency: secondsToMs(percentiles?.p90 ?? 0),
    minLatency: secondsToMs(summary?.min ?? 0),
    maxLatency: secondsToMs(summary?.max ?? 0),
    stdDev: secondsToMs(dispersion?.std_dev ?? 0),
    numOutliers: outliers?.count ?? 0,
    meanLatency: secondsToMs(summary?.mean ?? 0),
  }
}

function buildStatsFromLegacy(
  data: LatencyResultJson | null | undefined,
): LatencyStatsDisplay | null {
  const statistics = data?.latency_analysis?.statistics
  if (!statistics) {
    return null
  }
  const percentiles = statistics.distribution_metrics?.percentiles ?? {}
  const outlierMetadata = statistics.outlier_analysis?.metadata ?? {}
  return {
    medianLatency: secondsToMs(statistics.median_latency || 0),
    p90Latency: secondsToMs(percentiles.p90 || 0),
    minLatency: secondsToMs(statistics.min_latency || 0),
    maxLatency: secondsToMs(statistics.max_latency || 0),
    stdDev: secondsToMs(statistics.std_dev || 0),
    numOutliers: outlierMetadata.outlier_count || 0,
    meanLatency: secondsToMs(
      statistics.average_latency || statistics.mean_latency || 0,
    ),
  }
}

function resolveLatencyStats(
  mo: RunMetricOutput | MetricOutputDisplaySource,
): LatencyStatsDisplay | null {
  return (
    buildStatsFromTyped(mo) ??
    buildStatsFromLegacy(parseResultJson<LatencyResultJson>(mo))
  )
}

function resolveMeasurements(mo: RunMetricOutput | MetricOutputDisplaySource): {
  measurements: LatencyMeasurement[]
  filterRole: string | undefined
} {
  const legacy = parseResultJson<LatencyResultJson>(mo)
  const rawMeasurements = legacy?.latency_analysis?.latencies ?? []
  return {
    measurements: rawMeasurements.map((m) => ({
      latencyInSeconds: m.latency || 0,
      silenceStart: m.silence_start || 0,
      silenceEnd: m.silence_end || 0,
      toSpeaker: m.to_speaker || "unknown",
    })),
    filterRole: legacy?.filter_role,
  }
}

/** Format latency for display: always "X.XX s". */
function formatLatencySeconds(seconds: number): string {
  return `${seconds.toFixed(2)} s`
}

export class LatencyMetricRecord extends NumericalMetricRecord {
  documentation = DOCUMENTATION
  overviewSize = "medium" as const
  unit = "s"
  aggregate_units = "s"

  protected _formatValue(v: number): string {
    return v.toFixed(2)
  }

  // ─── Audio regions ────────────────────────────────────────────────────────

  getAudioRegions(
    mo: MetricOutputDisplaySource,
    _options?: { theme?: "light" | "dark" },
  ): AudioRegionParams[] {
    const colorForLatency = (latency: number) =>
      latency > 5 //threshold value
        ? "rgba(248, 113, 113, 0.4)"
        : latency > 3 // threshold value
          ? "rgba(251, 146, 60, 0.4)"
          : "rgba(74, 222, 128, 0.4)"

    try {
      const legacy = parseResultJson<LatencyResultJson>(mo)
      const filterRole = legacy?.filter_role

      // Prefer subvalues_by_timestamp.
      const subvalues: SubvalueByTimestamp[] = mo.subvalues_by_timestamp ?? []
      if (subvalues && subvalues.length > 0) {
        return subvalues
          .filter(
            (sv) =>
              (filterRole == null || sv.metadata?.to_speaker === filterRole) &&
              (sv.float_value ?? 0) >= MIN_LATENCY_THRESHOLD,
          )
          .map((sv, index) => {
            const latency = sv.float_value ?? 0
            return {
              id: `latency:${index}`,
              start: sv.start_offset,
              end: sv.end_offset,
              color: colorForLatency(latency),
              drag: false,
              resize: false,
              content: formatLatencySeconds(latency),
            }
          })
      }

      // Legacy fallback: result_json.latency_analysis.latencies.
      const { measurements } = resolveMeasurements(mo)
      if (!measurements.length) {
        return []
      }
      return measurements
        .filter(
          (m: LatencyMeasurement) =>
            m.toSpeaker === filterRole &&
            m.latencyInSeconds >= MIN_LATENCY_THRESHOLD,
        )
        .map((m: LatencyMeasurement, index: number) => ({
          id: `latency:${index}`,
          start: m.silenceStart,
          end: m.silenceEnd,
          color: colorForLatency(m.latencyInSeconds),
          drag: false,
          resize: false,
          content: formatLatencySeconds(m.latencyInSeconds),
        }))
    } catch {
      return []
    }
  }

  // ─── Human review ──────────────────────────────────────────────────────────

  getAudioRegionReviewConfig(): AudioRegionReviewConfig | null {
    return {
      category: CATEGORY,
      scoreType: "mean_seconds",
      useRegions: true,
      buildReviewedResultJson: ({ editedCount, score, metricOutput }) => ({
        mean_latency: Number(score.toFixed(3)),
        total_measurements: editedCount,
        filter_role: metricOutput?.result_json?.filter_role ?? null,
      }),
    }
  }

  // ─── Deep dive ────────────────────────────────────────────────────────────

  renderDeepDivePanel(
    notesNode: React.ReactNode,
    metricOutput: RunMetricOutput | undefined,
  ): React.ReactNode {
    if (!metricOutput) {
      return null
    }
    const statistics = resolveLatencyStats(metricOutput)
    if (!statistics) {
      return null
    }

    return (
      <div className="grid grid-cols-2 gap-6">
        <div className="grid grid-cols-2 gap-4">
          <ThresholdMetricBox
            title="Mean Latency"
            value={statistics.meanLatency / 1000}
            ranges={{ danger: 4.5, warning: 2.5 }}
            formatValue={(v) => `${v.toFixed(2)} s`}
          />
          <ThresholdMetricBox
            title="Standard Deviation"
            value={statistics.stdDev / 1000}
            ranges={{ danger: 1.5, warning: 0.5 }}
            formatValue={(v) => `${v.toFixed(2)} s`}
          />
          <ThresholdMetricBox
            title="Min Latency"
            value={statistics.minLatency / 1000}
            ranges={{ danger: 3, warning: 2 }}
            formatValue={(v) => `${v.toFixed(2)} s`}
          />
          <ThresholdMetricBox
            title="P90 Latency"
            value={statistics.p90Latency / 1000}
            ranges={{ danger: 6, warning: 3 }}
            formatValue={(v) => `${v.toFixed(2)} s`}
          />
        </div>
        <div>{notesNode}</div>
      </div>
    )
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    const statistics = resolveLatencyStats(mo)
    if (!statistics) {
      return <div>No detailed latency data available</div>
    }

    return (
      <div key={this.metric_name} className="w-full">
        <div className="mb-4">
          <div className="grid grid-cols-2 gap-4">
            <ThresholdMetricBox
              title="Mean Latency"
              value={statistics.meanLatency / 1000}
              ranges={{ danger: 4.5, warning: 2.5 }}
              formatValue={(v) => `${v.toFixed(2)} s`}
            />
            <ThresholdMetricBox
              title="Standard Deviation"
              value={statistics.stdDev / 1000}
              ranges={{ danger: 1.5, warning: 0.5 }}
              formatValue={(v) => `${v.toFixed(2)} s`}
            />
            <ThresholdMetricBox
              title="Min Latency"
              value={statistics.minLatency / 1000}
              ranges={{ danger: 3, warning: 2 }}
              formatValue={(v) => `${v.toFixed(2)} s`}
            />
            <ThresholdMetricBox
              title="P90 Latency"
              value={statistics.p90Latency / 1000}
              ranges={{ danger: 6, warning: 3 }}
              formatValue={(v) => `${v.toFixed(2)} s`}
            />
          </div>
        </div>
      </div>
    )
  }
}
